import { ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import useMcpServers from '@/components/pages/MCPs/hooks/useMcpServers';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { Fragment as KnowledgeFragment, ToolInvocation } from '@/shared/types/tryIt';

import { DetailRowSpec, ExpandToggle, NewTabStepLink, PlainRow } from './DetailsRow';
import { SearchMode, SourceList } from './DocumentsMatched';
import { buildKnowledgeSources } from './knowledgeSources';
import {
  CallGroup,
  CallJson,
  CallPanel,
  FailureNote,
  formatValue,
  OutcomeBadge,
  outcomeIconClass,
  ValueRow,
  ViewJsonToggle,
} from './StepRows';
import { selectBrainId } from '../../../redux/selectors';
import { ConversationTurn } from '../../../types/conversation';

export const keyToolCalls = (invocations: ToolInvocation[]) =>
  invocations.map((invocation, index) => ({
    key: `${invocation.mcp_id}-${invocation.name}-${index}`,
    invocation,
  }));

const NAME_SLOT = '\u0000';
const SOURCE_SLOT = '\u0001';

const SYNTHETIC_SERVER_KEY: Record<string, string> = {
  '004facab-37b4-5aa5-957b-4fd4ed112d0a': 'mcp_tools.004facab-37b4-5aa5-957b-4fd4ed112d0a',
  '1b3ad188-3c08-5eea-970d-0a49430fb31f': 'mcp_tools.1b3ad188-3c08-5eea-970d-0a49430fb31f',
  'dc8d655a-ae4b-5552-92da-c90cc14be729': 'mcp_tools.dc8d655a-ae4b-5552-92da-c90cc14be729',
  'feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc': 'mcp_tools.feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc',
};

const MOVEO_SERVER_IDS = new Set([
  '1b3ad188-3c08-5eea-970d-0a49430fb31f',
  'feb8e1ef-e9e0-526b-9a3d-8b3f9ae2a5fc',
]);

const KNOWLEDGE_TOOL = 'search_knowledge_base';
const WORKFLOW_TOOL = 'trigger_workflow';

const SEARCH_HEAD_KEY: Record<SearchMode, string> = {
  semantic: 'try_it.details.searched_semantic',
  keyword: 'try_it.details.searched_keyword',
};

const ToolLink = ({ mcpId, children }: { mcpId: string; children: ReactNode }) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const brainId = useSelector(selectBrainId);
  const hasPage = Boolean(slug && brainId) && !SYNTHETIC_SERVER_KEY[mcpId];

  return (
    <NewTabStepLink
      to={hasPage ? `/${slug}/ai-agents/${brainId}/mcps/${mcpId}` : null}
      tooltip={t('try_it.details.go_to_tool')}
    >
      {children}
    </NewTabStepLink>
  );
};

const useSourceName = (invocation: ToolInvocation) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const { mcpServers } = useMcpServers(brainId);

  const server = mcpServers.find((candidate) => candidate.mcp_id === invocation.mcp_id);
  const syntheticKey = SYNTHETIC_SERVER_KEY[invocation.mcp_id];

  return server?.name ?? (syntheticKey ? t(syntheticKey) : invocation.mcp_id);
};

const ToolHead = ({ invocation }: { invocation: ToolInvocation }) => {
  const { t } = useTranslation();
  const sourceName = useSourceName(invocation);
  const [before, rest = ''] = t('try_it.details.tool_invocation_from', {
    0: NAME_SLOT,
    1: SOURCE_SLOT,
  }).split(NAME_SLOT);
  const [between, after = ''] = rest.split(SOURCE_SLOT);

  return (
    <PlainRow>
      {before}
      <ToolLink mcpId={invocation.mcp_id}>{invocation.name}</ToolLink>
      {between}
      <span className="text-sm leading-none font-medium text-foreground">{sourceName}</span>
      {after}
    </PlainRow>
  );
};

const searchMode = (invocation: ToolInvocation): SearchMode =>
  invocation.arguments?.mode === 'keyword' ? 'keyword' : 'semantic';

const searchQueries = (invocation: ToolInvocation): string[] => {
  const queries = invocation.arguments?.queries;
  if (!Array.isArray(queries)) {
    return [];
  }
  return [...new Set(queries.filter((query): query is string => typeof query === 'string'))];
};

const isKnowledgeCall = (invocation: ToolInvocation) =>
  invocation.name === KNOWLEDGE_TOOL &&
  MOVEO_SERVER_IDS.has(invocation.mcp_id) &&
  invocation.succeeded;

/** A stored call has no `meta`, so one search in a turn borrows that turn's own retrieval. */
const searchFragments = (
  invocation: ToolInvocation,
  turn?: ConversationTurn
): KnowledgeFragment[] => {
  const fragments = invocation.meta?.fragments;
  if (Array.isArray(fragments)) {
    return fragments.filter(
      (fragment): fragment is KnowledgeFragment =>
        typeof (fragment as KnowledgeFragment)?.document_id === 'string'
    );
  }

  const searches = (turn?.metadata.toolCalls ?? []).filter(isKnowledgeCall).length;
  return searches === 1 ? (turn?.metadata.collection?.fragments ?? []) : [];
};

const KnowledgeHead = ({
  invocation,
  retrieved,
  selected,
}: {
  invocation: ToolInvocation;
  retrieved: number;
  selected: number;
}) => {
  const { t } = useTranslation();

  return (
    <PlainRow>
      {t(SEARCH_HEAD_KEY[searchMode(invocation)])}
      {retrieved > 0 &&
        ` · ${t('try_it.details.sources_retrieved', { count: retrieved })}, ${t('try_it.details.sources_selected_short', { count: selected })}`}
    </PlainRow>
  );
};

const KnowledgeBody = ({
  invocation,
  collectionId,
  sources,
}: {
  invocation: ToolInvocation;
  collectionId?: string;
  sources: ReturnType<typeof buildKnowledgeSources>['sources'];
}) => {
  const { t } = useTranslation();
  const queries = searchQueries(invocation);

  return (
    <div className="flex flex-col items-start gap-3 self-stretch">
      {queries.length > 0 && (
        <CallGroup label={t('try_it.details.queries')}>
          <div className="flex flex-wrap items-start gap-1.5">
            {queries.map((query) => (
              <span
                key={query}
                className="rounded-full border border-border bg-background px-2.5 py-0.5 text-sm leading-5 text-foreground"
              >
                {query}
              </span>
            ))}
          </div>
        </CallGroup>
      )}
      {collectionId && (
        <SourceList collectionId={collectionId} sources={sources} mode={searchMode(invocation)} />
      )}
    </div>
  );
};

const resultText = (invocation: ToolInvocation): string => {
  const content = invocation.content?.length ? invocation.content : invocation.result?.content;
  if (!Array.isArray(content)) {
    return '';
  }

  return content
    .map((part) => (part as { text?: unknown })?.text)
    .filter((text): text is string => typeof text === 'string')
    .join('\n\n');
};

const TextBlock = ({ children }: { children: string }) => (
  <span className="line-clamp-6 self-stretch text-sm leading-5 whitespace-pre-wrap text-muted-foreground">
    {children}
  </span>
);

const ValueRows = ({ values }: { values: Record<string, unknown> }) => (
  <>
    {Object.entries(values).map(([name, value]) => (
      <ValueRow key={name} name={name} value={formatValue(value)} />
    ))}
  </>
);

const hasArguments = (invocation: ToolInvocation) =>
  Object.keys(invocation.arguments ?? {}).length > 0;

const resultValues = (result: ToolInvocation['result']) =>
  Object.fromEntries(
    Object.entries(result ?? {}).filter(([name]) => name !== 'content' && name !== 'isError')
  );

const hasResult = (invocation: ToolInvocation) =>
  Boolean(resultText(invocation)) || Object.keys(resultValues(invocation.result)).length > 0;

const splitFailure = (message: string) => {
  const [, code, details] = /^(.{1,40}?)\s+(?:—|·)\s+(.+)$/s.exec(message) ?? [];
  return code ? { code, details } : { details: message };
};

const hasReadableBody = (invocation: ToolInvocation) =>
  hasArguments(invocation) || (invocation.succeeded && hasResult(invocation));

const ArgumentsGroup = ({ invocation }: { invocation: ToolInvocation }) => {
  const { t } = useTranslation();

  if (!hasArguments(invocation)) {
    return null;
  }

  return (
    <CallGroup label={t('try_it.details.arguments')}>
      <ValueRows values={invocation.arguments} />
    </CallGroup>
  );
};

const parsedFields = (text: string): Record<string, unknown> | undefined => {
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

const ResultGroup = ({ invocation }: { invocation: ToolInvocation }) => {
  const { t } = useTranslation();
  const text = resultText(invocation);
  const fields = text ? parsedFields(text) : undefined;
  const values = fields ?? resultValues(invocation.result);

  if (!hasResult(invocation)) {
    return null;
  }

  return (
    <CallGroup label={t('try_it.details.result')}>
      {text && !fields ? <TextBlock>{text}</TextBlock> : <ValueRows values={values} />}
    </CallGroup>
  );
};

type ToolRowProps = { invocation: ToolInvocation; turn?: ConversationTurn };

export const ToolRow = ({ invocation, turn }: ToolRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const failed = !invocation.succeeded;
  const isKnowledge = isKnowledgeCall(invocation);
  const failure = failed ? resultText(invocation) : '';
  const readable = failed
    ? Boolean(failure) || hasArguments(invocation)
    : hasReadableBody(invocation);
  const collectionId = turn?.metadata.knowledgeBase?.id ?? turn?.metadata.collection?.collection_id;
  const { sources, retrieved, selected } = buildKnowledgeSources(
    isKnowledge ? searchFragments(invocation, turn) : [],
    turn?.metadata.collection?.external_urls ?? [],
    { addMissingCited: false }
  );
  const payload = {
    arguments: invocation.arguments,
    content: invocation.content?.length ? invocation.content : undefined,
    result: invocation.result,
    meta: invocation.meta,
  };

  return (
    <div className="flex flex-col items-start gap-3 self-stretch">
      <div className="flex items-start justify-between gap-3 self-stretch">
        <span className="min-w-0 flex-1">
          {isKnowledge ? (
            <KnowledgeHead invocation={invocation} retrieved={retrieved} selected={selected} />
          ) : (
            <ToolHead invocation={invocation} />
          )}
        </span>
        <ExpandToggle
          expanded={expanded}
          onToggle={() => setExpanded((value) => !value)}
          label={t('common.show_more')}
        />
      </div>

      {expanded && (
        <div className="flex flex-col items-start gap-2 self-stretch pb-2">
          <div className="flex items-center justify-between self-stretch">
            <div className="flex items-center gap-1.5">
              {failed && (
                <OutcomeBadge flavour="destructive">{t('try_it.details.failed')}</OutcomeBadge>
              )}
              {typeof invocation.duration_ms === 'number' && (
                <OutcomeBadge className="border-border">
                  {t('try_it.details.execution_time', { 0: Math.round(invocation.duration_ms) })}
                </OutcomeBadge>
              )}
            </div>
            <ViewJsonToggle checked={showJson} onChange={setShowJson} subject={invocation.name} />
          </div>

          {showJson && (
            <CallPanel>
              <CallJson payload={payload} />
            </CallPanel>
          )}

          {!showJson && isKnowledge && (
            <KnowledgeBody invocation={invocation} collectionId={collectionId} sources={sources} />
          )}

          {!showJson && !isKnowledge && readable && (
            <CallPanel>
              {failure ? (
                <FailureNote {...splitFailure(failure)} />
              ) : (
                <>
                  <ArgumentsGroup invocation={invocation} />
                  {!failed && <ResultGroup invocation={invocation} />}
                </>
              )}
            </CallPanel>
          )}
        </div>
      )}
    </div>
  );
};

/** It only defers to a node, so the node's own row carries it. */
const isDeferredWorkflow = (invocation: ToolInvocation) =>
  invocation.name === WORKFLOW_TOOL &&
  MOVEO_SERVER_IDS.has(invocation.mcp_id) &&
  invocation.succeeded;

export const deferredWorkflowCall = (turn: ConversationTurn) =>
  (turn.metadata.toolCalls ?? []).find(isDeferredWorkflow);

export const TriggeredNodeDetail = ({ invocation }: { invocation: ToolInvocation }) => {
  const { t } = useTranslation();
  const [showJson, setShowJson] = useState(false);

  return (
    <div className="flex flex-col items-start gap-2 self-stretch pb-2">
      <div className="flex items-center justify-between self-stretch">
        <div className="flex items-center gap-1.5">
          {typeof invocation.duration_ms === 'number' && (
            <OutcomeBadge className="border-border">
              {t('try_it.details.execution_time', { 0: Math.round(invocation.duration_ms) })}
            </OutcomeBadge>
          )}
        </div>
        <ViewJsonToggle checked={showJson} onChange={setShowJson} subject={invocation.name} />
      </div>
      {showJson && (
        <CallPanel>
          <CallJson
            payload={{
              arguments: invocation.arguments,
              result: invocation.result,
              meta: invocation.meta,
            }}
          />
        </CallPanel>
      )}
    </div>
  );
};

export type ToolSource = 'agent' | 'moveo';

const sourceOf = (invocation: ToolInvocation): ToolSource =>
  MOVEO_SERVER_IDS.has(invocation.mcp_id) ? 'moveo' : 'agent';

export const buildToolRows = (turn: ConversationTurn, source?: ToolSource): DetailRowSpec[] =>
  keyToolCalls(turn.metadata.toolCalls ?? [])
    .filter(({ invocation }) => !isDeferredWorkflow(invocation))
    .filter(({ invocation }) => !source || sourceOf(invocation) === source)
    .map(({ key, invocation }) => ({
      key,
      icon: (
        <span className={outcomeIconClass(invocation.succeeded)}>
          <McpIcon size={16} />
        </span>
      ),
      head: <ToolRow invocation={invocation} turn={turn} />,
    }));
